import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ChatBubbleLeftIcon,
  CameraIcon,
  UserPlusIcon,
  MapPinIcon,
  BriefcaseIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { User } from '../../types';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';

interface QuickActionsProps {
  userType: 'creator' | 'model' | 'hirer';
}

const QuickActions: React.FC<QuickActionsProps> = ({ userType }) => {
  const creatorActions = [
    {
      title: 'Post Opportunity',
      description: 'Find models for your next shoot',
      icon: PlusIcon,
      to: '/opportunities/post',
      primary: true
    },
    {
      title: 'Find Models',
      description: 'Browse available models nearby',
      icon: MagnifyingGlassIcon,
      to: '/explore?user_type=model',
      primary: false
    },
    {
      title: 'Update Profile',
      description: 'Update your portfolio',
      icon: CameraIcon,
      to: '/profile/edit',
      primary: false
    },
    {
      title: 'Messages',
      description: 'Connect with collaborators',
      icon: ChatBubbleLeftIcon,
      to: '/messages',
      primary: false
    }
  ];

  const modelActions = [
    {
      title: 'Browse Opportunities',
      description: 'Find your next collaboration',
      icon: MagnifyingGlassIcon,
      to: '/opportunities',
      primary: true
    },
    {
      title: 'Find Creators',
      description: 'Connect with creators',
      icon: UserPlusIcon,
      to: '/explore?user_type=creator',
      primary: false
    },
    {
      title: 'Update Profile',
      description: 'Showcase your best work',
      icon: HeartIcon,
      to: '/profile/edit',
      primary: false
    },
    {
      title: 'Messages',
      description: 'Stay connected',
      icon: ChatBubbleLeftIcon,
      to: '/messages',
      primary: false
    }
  ];

  const hirerActions = [
    {
      title: 'Post Opportunity',
      description: 'Find talent for your next project',
      icon: PlusIcon,
      to: '/opportunities/post',
      primary: true
    },
    {
      title: 'Find Creators',
      description: 'Connect with creators',
      icon: UserPlusIcon,
      to: '/explore?user_type=creator',
      primary: false
    },
    {
      title: 'Update Profile',
      description: 'Showcase your company/profile',
      icon: CameraIcon,
      to: '/profile/edit',
      primary: false
    },
    {
      title: 'Messages',
      description: 'Stay connected',
      icon: ChatBubbleLeftIcon,
      to: '/messages',
      primary: false
    }
  ];

  const actions = userType === 'creator' ? creatorActions : userType === 'hirer' ? hirerActions : modelActions;

  return (
    <Card className="border-none shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="text-base md:text-lg font-bold">Quick Actions</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {actions.map((action, index) => (
            <motion.div
              key={action.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={action.to}
                className={`block p-4 md:p-5 rounded-2xl transition-all duration-200 group ${
                  action.primary 
                    ? 'bg-black text-white hover:bg-gray-900 dark:bg-white dark:text-black dark:hover:bg-gray-100 shadow-lg hover:shadow-xl hover:-translate-y-0.5' 
                    : 'bg-gray-50 text-gray-900 hover:bg-gray-100 border border-gray-100 hover:border-gray-200 dark:bg-gray-800 dark:text-white dark:border-gray-700 dark:hover:bg-gray-700'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`p-3 rounded-xl transition-colors ${
                    action.primary 
                      ? 'bg-white/10 group-hover:bg-white/20 dark:bg-black/5 dark:group-hover:bg-black/10' 
                      : 'bg-white shadow-sm group-hover:shadow dark:bg-gray-700 dark:shadow-none'
                  }`}>
                    <action.icon className={`h-6 w-6 ${action.primary ? 'text-white dark:text-black' : 'text-black dark:text-white'}`} />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="font-semibold text-xs md:text-sm mb-1">{action.title}</h3>
                    <p className={`text-[10px] md:text-xs leading-relaxed ${
                      action.primary ? 'text-gray-300 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {action.description}
                    </p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default QuickActions;
